if (import.meta.hot) {
  import.meta.hot.accept();
}

// Client-side filter bar, search, active-filter tags, pagination and
// copy-to-clipboard for the location-finder content element. Everything here
// runs against DOM already fully rendered server-side (LocationFinderPresenter
// resolves every location up front) — there is no server round-trip after
// the initial page load, per spec. The map (location-map.js, gated behind
// consent) reads the same rendered data independently and calls
// `window.locationFinderShowDetails(id)` to jump the list to one location.

(function () {
  'use strict';

  const CHECKBOX_SELECTOR = '.facet-option-item__checkbox-input';

  function setChecked(input, checked) {
    input.checked = checked;
    const visual = input.nextElementSibling;
    if (visual) {
      visual.classList.toggle('is-checked', checked);
    }
  }

  // Same "N selected" badge as EventFilterBar/event-list.js: a small pill
  // appended to (or removed from) `container` reflecting the current count.
  function upsertBadge(container, className, count, selectedLabel) {
    let badge = container.querySelector(`.${className}`);
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = className;
        container.appendChild(badge);
      }
      badge.textContent = String(count);
      badge.setAttribute('aria-label', `${count} ${selectedLabel}`);
    } else if (badge) {
      badge.remove();
    }
  }

  function updateCounters(filterBar, selectedLabel) {
    filterBar.querySelectorAll('[data-facet-dropdown]').forEach((dropdown) => {
      const toggle = dropdown.querySelector('.facet-dropdown__toggle');
      const labelWrap = dropdown.querySelector('.facet-dropdown__label');
      const count = dropdown.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`).length;
      if (toggle) {
        toggle.classList.toggle('has-selection', count > 0);
      }
      if (labelWrap) {
        upsertBadge(labelWrap, 'facet-dropdown__counter', count, selectedLabel);
      }
    });

    const toggleBtn = filterBar.querySelector('[data-filter-toggle]');
    if (toggleBtn) {
      const total = filterBar.querySelectorAll(`${CHECKBOX_SELECTOR}:checked`).length;
      upsertBadge(toggleBtn, 'search-filter-toggle__count', total, selectedLabel);
    }
  }

  const SUGGEST_MAX_RESULTS = 8;

  function initSearchSuggestions(filterBar, searchInput, onPick) {
    const suggestList = filterBar.querySelector('[data-location-search-suggest]');
    const namesScript = filterBar.querySelector('[data-location-company-names]');
    if (!suggestList || !namesScript) {
      return;
    }

    let companyNames = [];
    try {
      companyNames = JSON.parse(namesScript.textContent);
    } catch {
      companyNames = [];
    }

    // ARIA combobox pattern (input[role=combobox] → ul[role=listbox]): focus
    // stays in the input, the highlighted option is only announced via
    // aria-activedescendant — so options are plain role="option" items
    // rather than nested buttons, which a listbox must not contain.
    let activeIndex = -1;

    function options() {
      return Array.from(suggestList.querySelectorAll('[role="option"]'));
    }

    function setActive(index) {
      const items = options();
      activeIndex = index;
      items.forEach((item, i) => {
        const isActive = i === index;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          item.scrollIntoView({ block: 'nearest' });
        }
      });
      if (index >= 0 && items[index]) {
        searchInput.setAttribute('aria-activedescendant', items[index].id);
      } else {
        searchInput.removeAttribute('aria-activedescendant');
      }
    }

    function close() {
      suggestList.replaceChildren();
      suggestList.hidden = true;
      activeIndex = -1;
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.removeAttribute('aria-activedescendant');
    }

    function pick(name) {
      searchInput.value = name;
      close();
      onPick();
    }

    function renderSuggestions(query) {
      const matches = companyNames
        .filter((name) => name.toLowerCase().includes(query))
        .slice(0, SUGGEST_MAX_RESULTS);

      if (!matches.length) {
        close();
        return;
      }

      suggestList.replaceChildren();
      activeIndex = -1;
      searchInput.removeAttribute('aria-activedescendant');
      matches.forEach((name, index) => {
        const li = document.createElement('li');
        li.id = `${suggestList.id}-option-${index}`;
        li.className = 'search-form__suggest-item search-form__suggest-option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.textContent = name;
        // mousedown (not click) + preventDefault keeps focus in the input.
        li.addEventListener('mousedown', (event) => {
          event.preventDefault();
          pick(name);
        });
        suggestList.appendChild(li);
      });

      suggestList.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
    }

    searchInput.addEventListener('input', () => {
      const query = searchInput.value.trim().toLowerCase();
      if (!query) {
        close();
        return;
      }
      renderSuggestions(query);
    });

    document.addEventListener('click', (event) => {
      if (!filterBar.contains(event.target)) {
        close();
      }
    });

    searchInput.addEventListener('keydown', (event) => {
      const items = options();
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowDown' && items.length) {
        event.preventDefault();
        setActive((activeIndex + 1) % items.length);
      } else if (event.key === 'ArrowUp' && items.length) {
        event.preventDefault();
        setActive(activeIndex <= 0 ? items.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter' && items[activeIndex]) {
        event.preventDefault();
        pick(items[activeIndex].textContent);
      }
    });
  }

  function initLocationFinder(root) {
    const filterBar = root.querySelector('[data-location-filter-bar]');
    const searchInput = root.querySelector('[data-location-search]');
    const distributorToggle = root.querySelector('[data-location-distributor-toggle]');
    const activeFilters = root.querySelector('[data-active-filters-list]');
    const overlay = root.querySelector('.category-filter__overlay');
    const selectedLabel = overlay?.dataset.selectedLabel || 'selected';
    const items = Array.from(root.querySelectorAll('[data-location-item]'));
    const countryGroups = Array.from(root.querySelectorAll('[data-country-group]'));
    const emptyMessage = root.querySelector('[data-location-empty]');
    const paginationNav = root.querySelector('[data-pagination]');
    const itemsPerPage = parseInt(root.dataset.itemsPerPage, 10) || 10;
    const hidePagination = root.dataset.hidePagination === '1';

    let currentPage = 1;

    function selectedValues(field) {
      return Array.from(filterBar.querySelectorAll(`${CHECKBOX_SELECTOR}[data-facet-field="${field}"]:checked`)).map((input) => input.value);
    }

    // `includeDistributors` off (default) hides distributor locations from
    // the results entirely; switching it on adds them back in alongside
    // everything else — it never restricts the list to *only* distributors.
    function matchesItem(item, search, regions, divisions, types, includeDistributors) {
      if (search && !item.dataset.companyName.toLowerCase().includes(search)) {
        return false;
      }
      if (regions.length && !regions.includes(item.dataset.region)) {
        return false;
      }
      if (divisions.length && !divisions.includes(item.dataset.division)) {
        return false;
      }
      if (types.length) {
        const itemTypes = item.dataset.typeIds ? item.dataset.typeIds.split(',') : [];
        if (!types.some((type) => itemTypes.includes(type))) {
          return false;
        }
      }
      if (!includeDistributors && item.dataset.distributor === '1') {
        return false;
      }
      return true;
    }

    // Active-filter tags mirror only the facet dropdown selections (region/
    // division/type) — matching the Figma design, search and the distributor
    // toggle already show their own state inline (the typed text, the switch
    // position) and don't get a redundant removable chip.
    function buildTags(regions, divisions, types) {
      const tags = [];

      regions.forEach((value) => {
        const label = filterBar.querySelector(`[data-facet-field="region"][value="${value}"]`)?.closest('label')?.querySelector('.facet-option-item__label')?.textContent;
        tags.push({ label, onRemove: () => setChecked(filterBar.querySelector(`[data-facet-field="region"][value="${value}"]`), false) });
      });
      divisions.forEach((value) => {
        const label = filterBar.querySelector(`[data-facet-field="division"][value="${value}"]`)?.closest('label')?.querySelector('.facet-option-item__label')?.textContent;
        tags.push({ label, onRemove: () => setChecked(filterBar.querySelector(`[data-facet-field="division"][value="${value}"]`), false) });
      });
      types.forEach((value) => {
        const label = filterBar.querySelector(`[data-facet-field="type"][value="${value}"]`)?.closest('label')?.querySelector('.facet-option-item__label')?.textContent;
        tags.push({ label, onRemove: () => setChecked(filterBar.querySelector(`[data-facet-field="type"][value="${value}"]`), false) });
      });

      return tags;
    }

    // Same markup/classes as EventFilterBar's chips (search-active-filters__tag
    // + xmark icon) so removable tags look identical to the events filter bar.
    const TAG_REMOVE_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" class="icon"><path fill="currentColor" d="M4.234 5.281a.756.756 0 0 1 0-1.062.754.754 0 0 1 1.063 0l6.719 6.719 6.718-6.72a.75.75 0 0 1 1.06 1.06l-6.719 6.719 6.719 6.719a.75.75 0 0 1-1.06 1.06l-6.718-6.72-6.72 6.719a.75.75 0 0 1-1.06-1.06l6.72-6.718-6.718-6.719z"/></svg>';

    function renderTags(tags) {
      if (!activeFilters) {
        return;
      }

      activeFilters.querySelectorAll('[data-active-filter-tag]').forEach((tag) => tag.remove());

      tags.forEach((tag) => {
        if (!tag.label) {
          return;
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'search-active-filters__tag';
        button.dataset.activeFilterTag = '';
        button.append(document.createTextNode(tag.label));
        button.insertAdjacentHTML('beforeend', TAG_REMOVE_ICON);
        button.addEventListener('click', () => {
          tag.onRemove();
          applyFilters();
        });
        activeFilters.insertBefore(button, activeFilters.firstChild);
      });

      activeFilters.hidden = tags.length === 0;
    }

    const PAGINATION_ARROW_ICONS = {
      prev: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" class="icon"><path fill="currentColor" d="M4.219 11.469a.747.747 0 0 0 0 1.06l5.25 5.252a.75.75 0 0 0 1.06-1.06l-3.97-3.971H19.25c.416 0 .75-.334.75-.75a.75.75 0 0 0-.75-.75H6.56l3.971-3.969a.75.75 0 0 0-1.06-1.06z"/></svg>',
      next: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" class="icon"><path fill="currentColor" d="M19.781 12.531a.747.747 0 0 0 0-1.06l-5.25-5.252a.75.75 0 0 0-1.06 1.06l3.97 3.968H4.75a.75.75 0 0 0-.75.75c0 .415.334.75.75.75h12.69l-3.968 3.969a.75.75 0 0 0 1.06 1.06z"/></svg>',
    };

    // A country's group heading only shows while it still has an item
    // actually visible on the current page — must be re-run whenever
    // pagination changes which items are hidden, not just on filter changes.
    function updateCountryGroups() {
      countryGroups.forEach((group) => {
        const groupItems = Array.from(group.querySelectorAll('[data-location-item]'));
        group.hidden = !groupItems.some((item) => !item.hidden);
      });
    }

    function goToPage(page, visibleItems) {
      currentPage = page;
      renderPagination(visibleItems);
      updateCountryGroups();
      root.querySelector('[data-location-results]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Same windowed-with-ellipsis page list as PaginationResultBuilder::fromCounts()
    // (packages/altana-base/Classes/Pagination/PaginationResultBuilder.php): always
    // show first, last, and the pages immediately around the current one.
    function buildVisiblePages(current, totalPages) {
      const visible = [...new Set([1, totalPages, current - 1, current, current + 1])]
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((a, b) => a - b);

      const pages = [];
      let previous = null;
      visible.forEach((page) => {
        if (previous !== null && page - previous > 1) {
          pages.push({ ellipsis: true });
        }
        pages.push({ number: page, current: page === current });
        previous = page;
      });
      return pages;
    }

    function renderPagination(visibleItems) {
      if (!paginationNav) {
        return;
      }

      const totalPages = Math.ceil(visibleItems.length / itemsPerPage);

      visibleItems.forEach((item, index) => {
        item.hidden = index < (currentPage - 1) * itemsPerPage || index >= currentPage * itemsPerPage;
      });

      paginationNav.innerHTML = '';

      if (hidePagination || totalPages <= 1) {
        paginationNav.hidden = true;
        return;
      }

      paginationNav.hidden = false;

      const list = document.createElement('ul');
      list.className = 'pagination__list';

      if (currentPage > 1) {
        const li = document.createElement('li');
        li.className = 'pagination__item';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pagination__link pagination__link--prev';
        button.setAttribute('aria-label', 'Previous page');
        button.insertAdjacentHTML('beforeend', PAGINATION_ARROW_ICONS.prev);
        button.addEventListener('click', () => goToPage(currentPage - 1, visibleItems));
        li.appendChild(button);
        list.appendChild(li);
      }

      buildVisiblePages(currentPage, totalPages).forEach((page) => {
        const li = document.createElement('li');
        li.className = 'pagination__item';

        if (page.ellipsis) {
          const span = document.createElement('span');
          span.className = 'pagination__ellipsis';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = '…';
          li.appendChild(span);
        } else if (page.current) {
          const span = document.createElement('span');
          span.className = 'pagination__link pagination__link--active';
          span.textContent = String(page.number);
          li.appendChild(span);
        } else {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pagination__link';
          button.textContent = String(page.number);
          button.addEventListener('click', () => goToPage(page.number, visibleItems));
          li.appendChild(button);
        }

        list.appendChild(li);
      });

      if (currentPage < totalPages) {
        const li = document.createElement('li');
        li.className = 'pagination__item';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pagination__link pagination__link--next';
        button.setAttribute('aria-label', 'Next page');
        button.insertAdjacentHTML('beforeend', PAGINATION_ARROW_ICONS.next);
        button.addEventListener('click', () => goToPage(currentPage + 1, visibleItems));
        li.appendChild(button);
        list.appendChild(li);
      }

      paginationNav.appendChild(list);
    }

    // Filter options flagged `data-requires-distributors` (the "Distributor"
    // type) only exist while the toggle is on; switching it off hides them
    // again and drops a selection so it can't keep filtering invisibly.
    function syncDistributorOptions(includeDistributors) {
      filterBar?.querySelectorAll('[data-requires-distributors]').forEach((option) => {
        option.hidden = !includeDistributors;
        if (!includeDistributors) {
          const input = option.querySelector(CHECKBOX_SELECTOR);
          if (input?.checked) {
            setChecked(input, false);
          }
        }
      });
    }

    function applyFilters(resetPage = true) {
      const search = (searchInput?.value || '').trim().toLowerCase();
      const includeDistributors = !!distributorToggle?.checked;
      syncDistributorOptions(includeDistributors);
      const regions = selectedValues('region');
      const divisions = selectedValues('division');
      const types = selectedValues('type');

      if (resetPage) {
        currentPage = 1;
      }

      const visibleItems = [];
      items.forEach((item) => {
        if (matchesItem(item, search, regions, divisions, types, includeDistributors)) {
          visibleItems.push(item);
        } else {
          item.hidden = true;
        }
      });

      if (emptyMessage) {
        emptyMessage.hidden = visibleItems.length > 0;
      }

      // The map (location-map.js) filters its pins to the same set. The last
      // state is kept on `window` too, as the map may initialise (after
      // consent) later than this runs.
      const visibleIds = visibleItems.map((item) => item.dataset.locationId);
      window.locationFinderVisibleIds = visibleIds;
      document.dispatchEvent(new CustomEvent('location-finder:filter', { detail: { ids: visibleIds } }));

      renderPagination(visibleItems);
      updateCountryGroups();
      renderTags(buildTags(regions, divisions, types));
      if (filterBar) {
        updateCounters(filterBar, selectedLabel);
      }
    }

    function resetAll() {
      filterBar.querySelectorAll(CHECKBOX_SELECTOR).forEach((input) => setChecked(input, false));
      if (searchInput) {
        searchInput.value = '';
      }
      if (distributorToggle) {
        distributorToggle.checked = false;
      }
      applyFilters();
    }

    if (filterBar) {
      filterBar.querySelectorAll(CHECKBOX_SELECTOR).forEach((input) => {
        input.addEventListener('change', () => {
          setChecked(input, input.checked);
          applyFilters();
        });
      });

      filterBar.querySelectorAll('[data-reset-filters]').forEach((button) => {
        button.addEventListener('click', resetAll);
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', () => applyFilters());
      if (filterBar) {
        initSearchSuggestions(filterBar, searchInput, applyFilters);
      }
    }

    if (distributorToggle) {
      distributorToggle.addEventListener('change', () => applyFilters());
    }

    root.addEventListener('click', (event) => {
      const copyTrigger = event.target.closest('[data-copy-trigger]');
      if (!copyTrigger) {
        return;
      }

      const item = copyTrigger.closest('[data-location-item]');
      const target = item?.querySelector(`[data-copy-source="${copyTrigger.dataset.copyTarget}"]`);

      if (!target) {
        return;
      }

      const text = target.innerText.trim();
      copyText(text, copyTrigger);
    });

    function copyText(text, trigger) {
      const done = () => {
        const original = trigger.textContent;
        trigger.textContent = trigger.dataset.copiedLabel || original;
        window.setTimeout(() => {
          trigger.textContent = original;
        }, 2000);
      };

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => copyViaTextarea(text, done));
      } else {
        copyViaTextarea(text, done);
      }
    }

    function copyViaTextarea(text, done) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        done();
      } finally {
        textarea.remove();
      }
    }

    // Called by location-map.js when a single-location pin's "Show Details"
    // button is clicked: clears every active filter (so the target item can't
    // be hidden by a stale selection), jumps to whichever page it falls on if
    // pagination is active (renderPagination only un-hides the current page's
    // items — opening a card doesn't matter if it's still `hidden`), then
    // scrolls to and opens its card (a native <details data-location-item>,
    // per the event-card markup).
    window.locationFinderShowDetails = function locationFinderShowDetails(id) {
      // The map only shows distributor pins while the toggle is on, so keep
      // that state across the reset instead of silently flipping it.
      const includeDistributors = !!distributorToggle?.checked;
      resetAll();
      if (distributorToggle && includeDistributors) {
        distributorToggle.checked = true;
        applyFilters();
      }
      const item = root.querySelector(`[data-location-id="${id}"]`);
      if (!item) {
        return;
      }

      // Only items that pass the current filters take part in pagination —
      // paging over all `items` would un-hide the filtered-out ones (e.g.
      // distributors while the toggle is off).
      const visibleIds = window.locationFinderVisibleIds || [];
      const visibleItems = items.filter((other) => visibleIds.includes(other.dataset.locationId));
      if (!visibleItems.includes(item)) {
        return;
      }

      if (!hidePagination && itemsPerPage > 0) {
        goToPage(Math.floor(visibleItems.indexOf(item) / itemsPerPage) + 1, visibleItems);
      }

      items.forEach((other) => {
        other.open = other === item;
      });
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    applyFilters();
  }

  function init() {
    document.querySelectorAll('[data-location-finder]').forEach(initLocationFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

import './location-finder.scss';
